import { Component, computed, effect, inject, input, OnDestroy, signal } from '@angular/core';
import { AlertComponent, IconComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import type { DropOffLocation } from '../../../../../core/api/locator/models/locator.model';
import { TranslatePipe, TranslationService } from '../../../../../core/i18n';
import { DelayedLoadingController } from '../../../../../shared/utils/delayed-loading';
import { environment } from '../../../../../../environments/environment';
import { buildHomeLocatorUrl } from '../mappers/build-home-locator-url';
import {
  formatDisplayPostalCode,
  formatLocatorAddressLines,
  formatLocatorClockTime,
  formatLocatorDistanceKm,
  formatLocatorHoursRange,
  orderLocatorHours,
} from '../mappers/format-nearest-drop-off';
import { mapAvailableServiceKeys } from '../mappers/map-available-services';
import { NearestDropOffSkeleton } from './nearest-drop-off-skeleton/nearest-drop-off-skeleton';

const DAY_I18N_KEYS: Record<string, string> = {
  Mon: 'createShipment.serviceStep.pickupDropOff.nearestLocation.days.monday',
  Tue: 'createShipment.serviceStep.pickupDropOff.nearestLocation.days.tuesday',
  Wed: 'createShipment.serviceStep.pickupDropOff.nearestLocation.days.wednesday',
  Thu: 'createShipment.serviceStep.pickupDropOff.nearestLocation.days.thursday',
  Fri: 'createShipment.serviceStep.pickupDropOff.nearestLocation.days.friday',
  Sat: 'createShipment.serviceStep.pickupDropOff.nearestLocation.days.saturday',
  Sun: 'createShipment.serviceStep.pickupDropOff.nearestLocation.days.sunday',
};

/**
 * Nearest drop-off location card.
 * Displays locator search results as guidance only — not a customer selection.
 * Uses a delayed skeleton while the locator request is pending (independent of delivery rates).
 */
@Component({
  selector: 'app-nearest-drop-off-location',
  imports: [
    TypographyComponent,
    AlertComponent,
    IconComponent,
    TranslatePipe,
    NearestDropOffSkeleton,
  ],
  templateUrl: './nearest-drop-off-location.component.html',
  styleUrl: './nearest-drop-off-location.component.scss',
})
export class NearestDropOffLocationComponent implements OnDestroy {
  private readonly translation = inject(TranslationService);

  /** Validated Ship From postal used in the heading and Home Locator links. */
  readonly postalCode = input.required<string>();
  readonly location = input<DropOffLocation | null>(null);
  readonly loading = input(false);
  readonly errorMessage = input<string | null>(null);

  /** True when the delayed nearest-drop-off skeleton should paint. */
  protected readonly showSkeleton = signal(false);
  private readonly delayedLocatorLoading = new DelayedLoadingController((show) => {
    this.showSkeleton.set(show);
  });

  constructor() {
    effect(() => {
      this.delayedLocatorLoading.setPending(this.loading());
    });
  }

  /**
   * Clears delayed skeleton timers when the nearest-drop-off card is destroyed.
   */
  ngOnDestroy(): void {
    this.delayedLocatorLoading.destroy();
  }

  /**
   * Available Services i18n keys derived from locator capability flags (lead-table order).
   */
  protected readonly availableServiceKeys = computed(() =>
    mapAvailableServiceKeys(this.location()),
  );

  /**
   * Builds the Home Locator URL for View Map / Find other locations (`?postalcode=` prepopulation).
   */
  protected readonly homeLocatorUrl = computed(() => {
    this.translation.revision();
    return buildHomeLocatorUrl(
      environment.homeLocatorBaseUrl,
      this.postalCode(),
      this.translation.locale(),
    );
  });

  /** Postal formatted for the card heading. */
  protected readonly displayPostal = computed(() => formatDisplayPostalCode(this.postalCode()));

  /** Distance label (ESN-rounded km) for the active location. */
  protected readonly distanceLabel = computed(() => {
    const loc = this.location();
    return loc ? formatLocatorDistanceKm(loc.radialDistanceInKM) : '';
  });

  /** Two-line address for the pin row. */
  protected readonly addressLines = computed(() => {
    const loc = this.location();
    return loc ? formatLocatorAddressLines(loc) : { line1: '', line2: '' };
  });

  /**
   * Ordered hours rows with localized day labels for the Hours of Operation column.
   */
  protected readonly hourRows = computed(() => {
    this.translation.revision();
    const loc = this.location();
    if (!loc) {
      return [];
    }
    const locale = this.translation.locale() === 'fr' ? 'fr' : 'en';
    return orderLocatorHours(loc.hoursOfOperation).map((row) => {
      const hoursLabel = formatLocatorHoursRange(row, locale);
      return {
        dayLabel: this.translation.translate(DAY_I18N_KEYS[row.day] ?? row.day),
        hoursLabel:
          hoursLabel ??
          this.translation.translate(
            'createShipment.serviceStep.pickupDropOff.nearestLocation.closed',
          ),
      };
    });
  });

  /**
   * Formats `dropOffs[0].time` for the Latest drop-off time row; null when absent or blank.
   */
  protected readonly latestDropOffTime = computed(() => {
    this.translation.revision();
    const raw = this.location()?.dropOffs?.[0]?.time?.trim();
    if (!raw) {
      return null;
    }
    const locale = this.translation.locale() === 'fr' ? 'fr' : 'en';
    return formatLocatorClockTime(raw, locale);
  });
}
